import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { PUBLIC_URL, AUTH_URL } from './../../const';
import { useAuth } from "./../../AuthContext";
import LanguageSwitcher from "../core/switch-language"; // language switcher

// --- i18n ---
const translations = {
    en: {
        sentToEmail: "Sent to Email",
        didntGet: "Didn't get the code?",
        resend: "Resend",
        resendIn: (s) => `Resend in ${s}s`,
        resending: "Resending...",
        resendFailed: "Failed to resend code.",
        resendError: "An error occurred. Please try again.",
        title: "Two Step Verification",
        note: "A verification code has been sent to your email. Please enter it below.",
        label6: "Type your 6-digit security code",
        verifying: "Verifying...",
        verifyBtn: "Verify My Account",
        genericError: "An error occurred. Please try again.",
    },
    ru: {
        sentToEmail: "Отправлено на email",
        didntGet: "Не получили код?",
        resend: "Отправить ещё раз",
        resendIn: (s) => `Повтор через ${s}с`,
        resending: "Отправляем...",
        resendFailed: "Не удалось отправить код.",
        resendError: "Произошла ошибка. Попробуйте ещё раз.",
        title: "Двухэтапная проверка",
        note: "Код подтверждения отправлен на вашу почту. Введите его ниже.",
        label6: "Введите 6-значный код безопасности",
        verifying: "Проверяем...",
        verifyBtn: "Подтвердить аккаунт",
        genericError: "Произошла ошибка. Попробуйте ещё раз.",
    },
    ua: {
        sentToEmail: "Надіслано на email",
        didntGet: "Не отримали код?",
        resend: "Надіслати ще раз",
        resendIn: (s) => `Повтор через ${s}с`,
        resending: "Надсилаємо...",
        resendFailed: "Не вдалося надіслати код.",
        resendError: "Сталася помилка. Спробуйте ще раз.",
        title: "Двоетапна перевірка",
        note: "Код підтвердження надіслано на вашу пошту. Введіть його нижче.",
        label6: "Введіть 6-значний код безпеки",
        verifying: "Перевіряємо...",
        verifyBtn: "Підтвердити обліковий запис",
        genericError: "Сталася помилка. Спробуйте ще раз.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// --- Email Mask ---
const EmailMasker = ({ fullEmail, t }) => {
    // simple mask: keep first 3 of local, first 4 of domain
    const maskEmail = (email) => {
        const [local, domain] = (email || "").split('@');
        if (!local || !domain) return email || "";
        const maskedLocal = local.slice(0, 3) + '**';
        const domainParts = domain.split('.');
        const maskedDomain = (domainParts[0] || '').slice(0, 4) + '**';
        const ext = domainParts.slice(1).join('.');
        return `${maskedLocal}@${maskedDomain}${ext ? `.${ext}` : ''}`;
    };
    const maskedEmail = useMemo(() => maskEmail(fullEmail), [fullEmail]);
    return (
        <div>
            <p>{t.sentToEmail}: <strong>{maskedEmail}</strong></p>
        </div>
    );
};

// --- Resend Button ---
const ResendButton = ({ email, lang, t }) => {
    const [countdown, setCountdown] = useState(30);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const handleResendCode = async () => {
        if (countdown > 0 || loading) return;
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const stored = localStorage.getItem("authToken");
            const authHeader = stored ? `Bearer ${stored}` : undefined;

            const response = await axios.post(
                `${AUTH_URL}resend-2fa-code`,
                { email },
                { headers: { Authorization: authHeader, "Accept-Language": lang } }
            );

            if (response.status === 200) {
                setCountdown(30);
                setError('');
                setMessage(response.data?.message || '');
            } else {
                setError(response.data?.error || t.resendFailed);
                setMessage('');
                setCountdown(30);
            }
        } catch (e) {
            setError(t.resendError);
            setMessage('');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (countdown > 0) {
            const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [countdown]);

    return (
        <>
            {error && <p className="alert alert-danger text-danger">{error}</p>}
            {message && <p className="alert alert-success text-success">{message}</p>}
            <div className="d-flex align-items-center">
                <p className="fs-4 mb-0 text-dark">{t.didntGet}</p>
                <a
                    className="text-primary fw-medium ms-2"
                    href="#"
                    onClick={countdown === 0 && !loading ? handleResendCode : undefined}
                    style={{ cursor: countdown === 0 && !loading ? 'pointer' : 'not-allowed' }}
                >
                    {loading ? t.resending : countdown > 0 ? t.resendIn(countdown) : t.resend}
                </a>
            </div>
        </>
    );
};

// --- 2FA Form ---
const TwoFactorForm = ({ lang, t }) => {
    const inputs = useRef([]);
    const [values, setValues] = useState(["", "", "", "", "", ""]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { confirm2FA, getEmail } = useAuth();
    const navigate = useNavigate();
    const email = getEmail();

    const handleChange = (index, e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            const newValues = [...values];
            newValues[index] = value.slice(-1);
            setValues(newValues);
            if (value && index < 5) inputs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").slice(0, 6);
        if (/^\d{6}$/.test(pasteData)) {
            setValues(pasteData.split(""));
            inputs.current[5]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !values[index] && index > 0) {
            inputs.current[index - 1]?.focus();
        }
    };

    const VerifyMyAccountButton = async () => {
        setError('');
        setLoading(true);
        try {
            // send array as is; backend can Array.isArray(code)
            const data = await confirm2FA(values, { headers: { "Accept-Language": lang } });
            if (data.status === 200) {
                if (data.user?.allow_access) {
                    navigate(data.forward);
                } else {
                    setError(data.error || t.genericError);
                }
            } else {
                setError(data.error || t.genericError);
            }
        } catch (err) {
            setError(t.genericError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form>
            {error && <p className="alert alert-danger text-danger">{error}</p>}

            <div className="mb-3">
                <label htmlFor="securityCode" className="form-label fw-semibold">
                    {t.label6}
                </label>
                <div className="d-flex align-items-center gap-2 gap-sm-3">
                    {values.map((value, index) => (
                        <input
                            key={index}
                            type="text"
                            className="form-control text-center"
                            value={value}
                            onChange={(e) => handleChange(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            maxLength="1"
                            ref={(el) => (inputs.current[index] = el)}
                            disabled={loading}
                        />
                    ))}
                </div>
            </div>

            <button
                type="button"
                className="btn btn-primary w-100 py-8 mb-4"
                onClick={VerifyMyAccountButton}
                disabled={loading}
            >
                {loading ? (
                    <span>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t.verifying}
          </span>
                ) : (
                    t.verifyBtn
                )}
            </button>

            <ResendButton email={email} lang={lang} t={t} />
        </form>
    );
};

// --- Page ---
const TwoFa = () => {
    const { getEmail } = useAuth();
    const email = getEmail();

    const [lang, setLang] = useState(getInitialLang());
    const t = useMemo(() => translations[lang] || translations.en, [lang]);

    useEffect(() => {
        document.documentElement.lang = lang;
        localStorage.setItem("lang", lang);
    }, [lang]);

    return (
        <>
            <div id="main-wrapper">
                <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                    <div className="position-relative z-index-5">
                        <div className="row">
                            <div className="col-xl-7 col-xxl-8">
                                <a href={`${PUBLIC_URL}`} className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                    <a href="https://my-sites-guard.com/" className="d-inline-block">
                                        <img src={`https://my-sites-guard.com/assets/img/home_logo.png`} className="dark-logo" alt="Logo-Dark" style={{ maxHeight: "40px" }} />
                                    </a>
                                </a>
                                <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                    <img src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`} alt="" className="img-fluid" width="500" />
                                </div>
                            </div>

                            <div className="col-xl-5 col-xxl-4">
                                <div className="card mb-0 shadow-none rounded-0 min-vh-100 h-100">
                                    <div className="auth-max-width mx-auto d-flex align-items-center w-100 h-100">
                                        <div className="card-body">
                                            {/* Language switcher */}
                                            <div className="d-flex justify-content-end mb-3">
                                                <LanguageSwitcher lang={lang} setLang={setLang} />
                                            </div>

                                            <div className="mb-5">
                                                <h3 className="fw-bolder fs-7 mb-3">{t.title}</h3>
                                                <p>{t.note}</p>
                                                <EmailMasker fullEmail={email} t={t} />
                                            </div>

                                            <TwoFactorForm lang={lang} t={t} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <div className="dark-transparent sidebartoggler"></div>

            {/* your scripts below if really needed on this page */}
            {/* <script src="./assets/libs/bootstrap/dist/js/bootstrap.bundle.min.js"></script> */}
        </>
    );
};

export default TwoFa;
